import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Locale JSONs are ~130 KB each; loading them on demand keeps the other two
// languages out of the initial bundle. The static loader map keeps the
// dynamic imports analyzable by Vite so each locale becomes its own chunk.
const localeLoaders: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  de: () => import('./locales/de.json'),
  en: () => import('./locales/en.json'),
  es: () => import('./locales/es.json'),
};

const lazyLocaleBackend = {
  type: 'backend' as const,
  init() {},
  read(
    language: string,
    _namespace: string,
    callback: (err: unknown, data: Record<string, unknown> | null) => void,
  ) {
    const load = localeLoaders[language];
    if (!load) {
      callback(new Error(`Unsupported language: ${language}`), null);
      return;
    }
    load()
      .then((mod) => callback(null, mod.default))
      .catch((err) => callback(err, null));
  },
};

// Get saved language or default to German
const savedLanguage = localStorage.getItem('language') || 'de';

i18n
  .use(lazyLocaleBackend)
  .use(initReactI18next)
  .init({
    lng: savedLanguage,
    fallbackLng: 'de',
    supportedLngs: ['de', 'en', 'es'],
    load: 'languageOnly',
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
  });

// Keep `<html lang>` in sync with the active i18n language so screen
// readers, search engines, and the browser's spellchecker pick the
// right language for content. `index.html` ships with a static lang="en"
// fallback for the initial paint; this overrides it the moment i18n
// resolves.
if (typeof document !== 'undefined') {
  document.documentElement.lang = i18n.language || 'de';
  i18n.on('languageChanged', (lng) => {
    document.documentElement.lang = lng;
  });
}

// Helper to change language and persist
export const changeLanguage = (lang: string) => {
  localStorage.setItem('language', lang);
  i18n.changeLanguage(lang);
};

export default i18n;
